/**
 * WebSocket Redux Slice
 *
 * Centralized state management for WebSocket connections and data.
 * Replaces direct cache invalidation with Redux-first updates.
 *
 * PHASE 0: Data Normalization
 * - All notifications now use unified structure
 * - Deduplication based on hash
 */

import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logRedux } from '../utils/logger';
import { isDuplicateNotification } from '../utils/notificationNormalizer';

const initialState = {
  // Connection states for different servers
  connections: {
    notifications: {
      connected: false,
      connecting: false,
      error: null,
      lastConnected: null,
      reconnectAttempts: 0
    },
    trading: {
      connected: false,
      connecting: false,
      error: null,
      lastConnected: null,
      reconnectAttempts: 0
    }
  },
  
  // Market data from trading WebSocket
  marketData: {
    tickers: {},
    orderbooks: {},
    trades: {},
    lastUpdate: null
  },
  
  // Wallet data from notifications WebSocket
  walletData: {
    balances: {},
    transactions: [],
    lastUpdate: null,
    updateTrigger: 0 // Increment to trigger React Query invalidation
  },
  
  // Order data from notifications WebSocket
  orderData: {
    orders: [],
    orderHistory: [],
    lastUpdate: null,
    updateTrigger: 0 // Increment to trigger React Query invalidation
  },
  
  // Notification data (consolidated from legacy notification slice)
  notifications: {
    messages: [],           // Real-time WebSocket notifications
    allNotifications: [],   // HTTP API notifications (full list)
    unreadCount: 0,
    isLoading: false,
    lastUpdate: null
  },
  
  // Service health
  serviceHealth: {
    isHealthy: false,
    lastHealthCheck: null,
    errors: []
  }
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Connection state management
    setConnectionState: (state, action) => {
      state.connections = { ...state.connections, ...action.payload };

      // Update service health
      const connectedCount = Object.values(state.connections).filter(conn => conn && conn.connected).length;
      state.serviceHealth.isHealthy = connectedCount > 0;
      state.serviceHealth.lastHealthCheck = Date.now();
    },
    
    setConnectionStatus: (state, action) => {
      const { serverKey, status } = action.payload;
      if (state.connections[serverKey]) {
        state.connections[serverKey] = { ...state.connections[serverKey], ...status };
      }
    },
    
    // Market data updates
    setMarketData: (state, action) => {
      const { type, data, timestamp } = action.payload;
      
      switch (type) {
        case 'ticker_update':
          if (data.symbol) {
            state.marketData.tickers[data.symbol] = {
              ...state.marketData.tickers[data.symbol],
              ...data,
              lastUpdate: timestamp
            };
          }
          break;
          
        case 'orderbook_update':
          logRedux('Processing orderbook_update', {
            dataType: typeof data,
            hasSymbol: data?.symbol,
            hasOrderbook: data?.orderbook,
            dataStructure: data
          });

          if (data.symbol && data.orderbook) {
            // New structure: { symbol: 'TRXUSDT', orderbook: {...}, timestamp: ... }
            logRedux('Storing orderbook for symbol:', data.symbol);
            state.marketData.orderbooks[data.symbol] = {
              ...data.orderbook,
              lastUpdate: timestamp
            };
          } else if (data.symbol) {
            // Fallback: Data has symbol but orderbook is at root level
            logRedux('Storing orderbook for symbol (fallback):', data.symbol);
            state.marketData.orderbooks[data.symbol] = {
              ...data,
              lastUpdate: timestamp
            };
          } else {
            console.warn('🌍 Redux: Unknown orderbook data structure:', data);
          }
          break;
          
        case 'trade_update':
          if (data.symbol) {
            if (!state.marketData.trades[data.symbol]) {
              state.marketData.trades[data.symbol] = [];
            }
            // Keep only last 100 trades per symbol
            state.marketData.trades[data.symbol] = [
              data,
              ...state.marketData.trades[data.symbol].slice(0, 99)
            ];
          }
          break;

        case 'deals_update':
          // Handle deals data from global WebSocket service
          logRedux('Processing deals_update', {
            dataType: typeof data,
            isArray: Array.isArray(data),
            dataKeys: typeof data === 'object' ? Object.keys(data) : 'N/A',
            hasSymbol: data?.symbol,
            hasDeals: data?.deals,
            dataStructure: data
          });

          if (data.symbol && Array.isArray(data.deals)) {
            // New structure: { symbol: 'TRXUSDT', deals: [...], timestamp: ... }
            logRedux('Storing deals for symbol:', data.symbol, 'Count:', data.deals.length);
            if (!state.marketData.trades[data.symbol]) {
              state.marketData.trades[data.symbol] = [];
            }
            state.marketData.trades[data.symbol] = data.deals.slice(0, 100);
          } else if (Array.isArray(data)) {
            // Fallback: Data is array of deals (try to extract symbol)
            const symbol = data[0]?.symbol || data[0]?.coin || 'UNKNOWN';
            logRedux('Storing deals for symbol (fallback):', symbol, 'Count:', data.length);

            if (symbol !== 'UNKNOWN') {
              if (!state.marketData.trades[symbol]) {
                state.marketData.trades[symbol] = [];
              }
              state.marketData.trades[symbol] = data.slice(0, 100);
            }
          } else {
            console.warn('🌍 Redux: Unknown deals data structure:', data);
          }
          break;
          
        case 'usdt_price_update':
        case 'indexprice_update':
          // Handle USDT price updates specifically
          if (data?.USDTIRT) {
            state.marketData = {
              ...state.marketData,
              USDTIRT: data.USDTIRT,
              lastUpdate: timestamp
            };
          }
          break;

        case 'market_update':
        default:
          // Generic market data update
          state.marketData = {
            ...state.marketData,
            ...data,
            lastUpdate: timestamp
          };
          break;
      }
      
      state.marketData.lastUpdate = timestamp;
    },
    
    // Wallet data updates
    setWalletData: (state, action) => {
      const { type, data, timestamp } = action.payload;
      
      switch (type) {
        case 'websocket_update':
          // Increment trigger to cause React Query invalidation
          state.walletData.updateTrigger += 1;
          state.walletData.lastUpdate = timestamp;
          
          // Store the update data for components that need it
          if (data.balances) {
            state.walletData.balances = { ...state.walletData.balances, ...data.balances };
          }
          break;
          
        case 'balance_update':
          if (data.symbol && data.balance !== undefined) {
            state.walletData.balances[data.symbol] = {
              ...state.walletData.balances[data.symbol],
              ...data,
              lastUpdate: timestamp
            };
          }
          state.walletData.updateTrigger += 1;
          state.walletData.lastUpdate = timestamp;
          break;
          
        case 'transaction_update':
          if (data.transaction) {
            state.walletData.transactions = [
              data.transaction,
              ...state.walletData.transactions.slice(0, 49) // Keep last 50
            ];
          }
          state.walletData.updateTrigger += 1;
          state.walletData.lastUpdate = timestamp;
          break;
          
        default:
          state.walletData.updateTrigger += 1;
          state.walletData.lastUpdate = timestamp;
          break;
      }
    },
    
    // Order data updates
    setOrderData: (state, action) => {
      const { type, data, timestamp } = action.payload;
      
      switch (type) {
        case 'websocket_update':
          // Increment trigger to cause React Query invalidation
          state.orderData.updateTrigger += 1;
          state.orderData.lastUpdate = timestamp;
          break;
          
        case 'order_update':
          if (data.order) {
            // Update or add order
            const orderIndex = state.orderData.orders.findIndex(o => o.id === data.order.id);
            if (orderIndex >= 0) {
              state.orderData.orders[orderIndex] = { ...state.orderData.orders[orderIndex], ...data.order };
            } else {
              state.orderData.orders = [data.order, ...state.orderData.orders.slice(0, 49)]; // Keep last 50
            }
          }
          state.orderData.updateTrigger += 1;
          state.orderData.lastUpdate = timestamp;
          break;
          
        case 'order_fill':
          if (data.orderId) {
            // Move filled order to history
            const orderIndex = state.orderData.orders.findIndex(o => o.id === data.orderId);
            if (orderIndex >= 0) {
              const filledOrder = state.orderData.orders[orderIndex];
              state.orderData.orders.splice(orderIndex, 1);
              state.orderData.orderHistory = [
                { ...filledOrder, status: 'filled', fillTime: timestamp },
                ...state.orderData.orderHistory.slice(0, 99) // Keep last 100
              ];
            }
          }
          state.orderData.updateTrigger += 1;
          state.orderData.lastUpdate = timestamp;
          break;
          
        default:
          state.orderData.updateTrigger += 1;
          state.orderData.lastUpdate = timestamp;
          break;
      }
    },
    
    // Notification updates
    setNotificationData: (state, action) => {
      const { type, data, timestamp, normalizedType } = action.payload;

      // Safety check - ensure data exists
      if (!data) {
        console.warn('⚠️ setNotificationData called with no data:', action.payload);
        return;
      }

      // 🔥 PHASE 0: Data is now already normalized by WebSocketService
      // The 'data' field contains a NormalizedNotification object with:
      // - id, title, message (not content), update_wallet, update_orders, silent, created_at, source, type, hash

      // Normalize type to handle both string and numeric types
      const messageType = normalizedType || (typeof data.type === 'string'
        ? data.type.toUpperCase()
        : data.type);

      // 🔥 PHASE 0: Check for duplicates before adding
      const isDuplicate = isDuplicateNotification(data, state.notifications.messages);

      if (isDuplicate) {
        console.log('🔄 Duplicate notification detected, skipping:', data.title);
        return;
      }

      // Only add to messages array if it's a general notification (type 0)
      if (messageType === 'NOTIFICATION' || data.type === 0) {
        // 🔥 PHASE 0: Store normalized notification structure
        state.notifications.messages = [
          {
            ...data,  // Spread normalized notification (already has all fields)
            read: false,
            timestamp: timestamp || Date.now(),
            normalizedType: messageType
          },
          ...state.notifications.messages.slice(0, 49) // Keep last 50
        ];
        state.notifications.unreadCount += 1;
      }
      // For type 1 (wallet) and type 2 (order), just update the lastUpdate timestamp
      // The actual data is handled by setWalletData and setOrderData reducers

      state.notifications.lastUpdate = timestamp || Date.now();
    },
    
    // Mark notification as read
    markNotificationRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.messages.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        notification.read = true;
        state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
      }
    },
    
    // Mark all notifications as read
    markAllNotificationsRead: (state) => {
      state.notifications.messages.forEach(notification => {
        notification.read = true;
      });
      state.notifications.unreadCount = 0;
    },

    // Set all notifications from HTTP API
    setAllNotifications: (state, action) => {
      state.notifications.allNotifications = action.payload;
      state.notifications.lastUpdate = Date.now();
    },

    // Set unread count from HTTP API
    setUnreadCount: (state, action) => {
      state.notifications.unreadCount = action.payload;
    },

    // Set loading state
    setNotificationsLoading: (state, action) => {
      state.notifications.isLoading = action.payload;
    },

    // Legacy compatibility - wallet updater
    handleWalletUpdater: (state) => {
      state.walletData.updateTrigger += 1;
    },

    // Legacy compatibility - order updater
    handleOrderUpdater: (state) => {
      state.orderData.updateTrigger += 1;
    },

    // Legacy compatibility - online status
    handleOnlineUser: (state, action) => {
      state.connections.notifications.connected = action.payload;
    },
    
    // Clear old data
    clearOldData: (state, action) => {
      const { maxAge = 3600000 } = action.payload; // Default 1 hour
      const cutoff = Date.now() - maxAge;

      // Clear old notifications
      state.notifications.messages = state.notifications.messages.filter(
        n => n.timestamp > cutoff
      );

      // Clear old trades
      Object.keys(state.marketData.trades).forEach(symbol => {
        state.marketData.trades[symbol] = state.marketData.trades[symbol].filter(
          trade => trade.timestamp > cutoff
        );
      });

      // Clear old transactions
      state.walletData.transactions = state.walletData.transactions.filter(
        t => t.timestamp > cutoff
      );
    },

    // Force refresh all UI components (for sleep/wake scenarios)
    forceRefresh: (state, action) => {
      const timestamp = action.payload?.timestamp || Date.now();

      // Increment all update triggers to force React Query invalidation
      state.walletData.updateTrigger += 1;
      state.orderData.updateTrigger += 1;
      state.marketData.lastUpdate = timestamp;
      state.walletData.lastUpdate = timestamp;
      state.orderData.lastUpdate = timestamp;

      // Update service health
      state.serviceHealth.lastHealthCheck = timestamp;

      console.log('🔄 Redux: Force refresh triggered', {
        walletTrigger: state.walletData.updateTrigger,
        orderTrigger: state.orderData.updateTrigger,
        timestamp
      });
    },

    // Force refresh all UI components (for sleep/wake scenarios)
    forceRefresh: (state, action) => {
      const timestamp = action.payload?.timestamp || Date.now();

      // Increment all update triggers to force React Query invalidation
      state.walletData.updateTrigger += 1;
      state.orderData.updateTrigger += 1;
      state.marketData.lastUpdate = timestamp;
      state.walletData.lastUpdate = timestamp;
      state.orderData.lastUpdate = timestamp;

      // Update service health
      state.serviceHealth.lastHealthCheck = timestamp;

      console.log('🔄 Redux: Force refresh triggered', {
        walletTrigger: state.walletData.updateTrigger,
        orderTrigger: state.orderData.updateTrigger,
        timestamp
      });
    },
    
    // Reset state
    resetWebSocketState: () => initialState,
    
    // Service health updates
    updateServiceHealth: (state, action) => {
      state.serviceHealth = { ...state.serviceHealth, ...action.payload };
    },
    
    addServiceError: (state, action) => {
      state.serviceHealth.errors = [
        {
          timestamp: Date.now(),
          error: action.payload
        },
        ...state.serviceHealth.errors.slice(0, 9) // Keep last 10 errors
      ];
      state.serviceHealth.isHealthy = false;
    }
  }
});

// Selectors with null safety
export const selectConnectionState = (state) => state.websocket?.connections || {};
export const selectIsConnected = (serverKey) => (state) =>
  state.websocket?.connections?.[serverKey]?.connected || false;
export const selectMarketData = (state) => state.websocket?.marketData || { tickers: {}, orderbooks: {}, trades: {}, lastUpdate: null };
export const selectWalletData = (state) => state.websocket?.walletData || { balances: {}, transactions: [], lastUpdate: null, updateTrigger: 0 };
export const selectOrderData = (state) => state.websocket?.orderData || { orders: [], orderHistory: [], lastUpdate: null, updateTrigger: 0 };
export const selectNotifications = (state) => state.websocket?.notifications || { messages: [], allNotifications: [], unreadCount: 0, isLoading: false, lastUpdate: null };
export const selectServiceHealth = (state) => state.websocket?.serviceHealth || { isHealthy: false, lastHealthCheck: null, errors: [] };

// Memoized selectors with null safety
export const selectTickerBySymbol = createSelector(
  [selectMarketData, (state, symbol) => symbol],
  (marketData, symbol) => {
    if (!marketData || !marketData.tickers) return null;
    return marketData.tickers[symbol] || null;
  }
);

export const selectOrderbookBySymbol = createSelector(
  [selectMarketData, (state, symbol) => symbol],
  (marketData, symbol) => {
    if (!marketData || !marketData.orderbooks) return null;
    return marketData.orderbooks[symbol] || null;
  }
);

export const selectWalletUpdateTrigger = createSelector(
  [selectWalletData],
  (walletData) => walletData?.updateTrigger || 0
);

export const selectOrderUpdateTrigger = createSelector(
  [selectOrderData],
  (orderData) => orderData?.updateTrigger || 0
);

export const selectUnreadNotificationCount = createSelector(
  [selectNotifications],
  (notifications) => notifications?.unreadCount || 0
);

export const selectIsAnyServerConnected = createSelector(
  [selectConnectionState],
  (connections) => {
    // Add null safety check to prevent "Cannot read properties of null" errors
    if (!connections || typeof connections !== 'object') {
      return false;
    }

    return Object.values(connections).some(conn => {
      // Ensure conn is not null and has connected property
      return conn && typeof conn === 'object' && conn.connected === true;
    });
  }
);

// Action exports
export const {
  setConnectionState,
  setConnectionStatus,
  setMarketData,
  setWalletData,
  setOrderData,
  setNotificationData,
  markNotificationRead,
  markAllNotificationsRead,
  setAllNotifications,
  setUnreadCount,
  setNotificationsLoading,
  handleWalletUpdater,
  handleOrderUpdater,
  handleOnlineUser,
  clearOldData,
  resetWebSocketState,
  updateServiceHealth,
  addServiceError
} = websocketSlice.actions;

// Legacy exports for backward compatibility
export const handleSetNotifications = setAllNotifications;

export default websocketSlice.reducer;
